import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus, Phone, Mail, MessageSquare, StickyNote } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface DealNote {
  id: string;
  deal_id: string;
  note_type: string;
  content: string;
  contact_date: string;
  created_at: string;
}

interface ActivityLogTabProps {
  dealId?: string;
}

const NOTE_TYPES = [
  { value: "call", label: "Phone Call", icon: Phone },
  { value: "email", label: "Email", icon: Mail },
  { value: "text", label: "Text/SMS", icon: MessageSquare },
  { value: "note", label: "Note", icon: StickyNote },
];

function typeMeta(type: string) {
  return NOTE_TYPES.find((t) => t.value === type) ?? NOTE_TYPES[3];
}

export function ActivityLogTab({ dealId }: ActivityLogTabProps) {
  const { toast } = useToast();
  const [notes, setNotes] = useState<DealNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [noteType, setNoteType] = useState("call");
  const [content, setContent] = useState("");
  const [contactDate, setContactDate] = useState(
    new Date().toISOString().slice(0, 16)
  );

  const load = async () => {
    if (!dealId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("deal_notes")
      .select("*")
      .eq("deal_id", dealId)
      .order("contact_date", { ascending: false });
    setLoading(false);
    if (error) {
      toast({ title: "Error loading notes", description: error.message, variant: "destructive" });
      return;
    }
    setNotes(data || []);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  const handleAdd = async () => {
    if (!dealId) {
      toast({ title: "Save the deal first", description: "You must save the deal before adding activity log entries.", variant: "destructive" });
      return;
    }
    if (!content.trim()) {
      toast({ title: "Content required", description: "Please write something before saving.", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from("deal_notes").insert({
      deal_id: dealId,
      note_type: noteType,
      content: content.trim(),
      contact_date: new Date(contactDate).toISOString(),
      created_by: user?.id ?? null,
    });
    setSaving(false);
    if (error) {
      toast({ title: "Error saving note", description: error.message, variant: "destructive" });
      return;
    }
    setContent("");
    setContactDate(new Date().toISOString().slice(0, 16));
    load();
    toast({ title: "Activity logged" });
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this entry?")) return;
    const { error } = await supabase.from("deal_notes").delete().eq("id", id);
    if (error) {
      toast({ title: "Error deleting", description: error.message, variant: "destructive" });
      return;
    }
    setNotes((prev) => prev.filter((n) => n.id !== id));
  };

  if (!dealId) {
    return (
      <div className="text-sm text-muted-foreground p-4">
        Save the deal first to start logging calls and conversations.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={noteType} onValueChange={setNoteType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NOTE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Date / Time</Label>
              <Input
                type="datetime-local"
                value={contactDate}
                onChange={(e) => setContactDate(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              rows={4}
              placeholder="What happened? Who did you talk to? Next steps..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>
          <div className="flex justify-end">
            <Button type="button" onClick={handleAdd} disabled={saving}>
              <Plus className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Add Entry"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-muted-foreground">
          History ({notes.length})
        </h3>
        {loading && <div className="text-sm text-muted-foreground">Loading...</div>}
        {!loading && notes.length === 0 && (
          <div className="text-sm text-muted-foreground italic">No activity logged yet.</div>
        )}
        {notes.map((n) => {
          const meta = typeMeta(n.note_type);
          const Icon = meta.icon;
          return (
            <Card key={n.id}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="mt-1 p-2 rounded-md bg-muted">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="secondary">{meta.label}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(n.contact_date), "MMM d, yyyy h:mm a")}
                        </span>
                      </div>
                      <div className="whitespace-pre-wrap text-sm">{n.content}</div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(n.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
